<template>
  <section class="user-directory">
    <div class="user-directory__toolbar">
      <div class="user-directory__filters">
        <label class="user-directory__filter user-directory__filter--compact">
          <span>Account Name</span>
          <input
            v-model="accountKeyword"
            type="text"
            class="form-control"
            placeholder="Filter by account name"
          >
        </label>

        <label class="user-directory__filter user-directory__filter--compact">
          <span>Full Name</span>
          <input
            v-model="realnameKeyword"
            type="text"
            class="form-control"
            placeholder="Filter by real name"
          >
        </label>

        <div class="user-directory__filter user-directory__filter--compact">
          <span>Privilege</span>
          <div ref="privilegePickerRef" class="user-search-select">
            <button
              type="button"
              class="form-control user-search-select-trigger"
              :aria-expanded="privilegePickerOpen ? 'true' : 'false'"
              @click.stop="togglePrivilegePicker"
            >
              <span class="user-search-select-trigger-label">{{ privilegeTriggerLabel }}</span>
              <span>{{ privilegePickerOpen ? '▴' : '▾' }}</span>
            </button>

            <div v-if="privilegePickerOpen" class="user-search-select-panel" @click.stop>
              <button type="button" class="user-search-select-option" @click="selectPrivilege('')">All</button>
              <button
                v-for="option in privilegeOptions"
                :key="`privilege-${option}`"
                type="button"
                class="user-search-select-option"
                @click="selectPrivilege(option)"
              >
                {{ option }}
              </button>
            </div>
          </div>
        </div>

        <div class="user-directory__filter user-directory__filter--compact">
          <span>Status</span>
          <div ref="statusPickerRef" class="user-search-select">
            <button
              type="button"
              class="form-control user-search-select-trigger"
              :aria-expanded="statusPickerOpen ? 'true' : 'false'"
              @click.stop="toggleStatusPicker"
            >
              <span class="user-search-select-trigger-label">{{ statusTriggerLabel }}</span>
              <span>{{ statusPickerOpen ? '▴' : '▾' }}</span>
            </button>

            <div v-if="statusPickerOpen" class="user-search-select-panel" @click.stop>
              <button type="button" class="user-search-select-option" @click="selectStatus('')">All</button>
              <button
                v-for="option in statusOptions"
                :key="`status-${option}`"
                type="button"
                class="user-search-select-option"
                @click="selectStatus(option)"
              >
                {{ option }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <p class="user-directory__count">{{ filteredRows.length }} / {{ rows.length }} users</p>
    </div>

    <AppPagination
      :pagination="pagination"
      aria-label="User directory pagination"
      key-prefix="users"
      extra-class="user-directory__pagination"
      @click.prevent="handlePaginationClick"
    />

    <div class="user-directory__table-wrap table-wrapper-scroll-y app-scroll-table">
      <table class="table table-striped table-bordered table-condensed">
        <thead>
          <tr>
            <th><div class="text-center">Account Name</div></th>
            <th class="hidden-phone"><div class="text-center">E-mail</div></th>
            <th class="hidden-tablet"><div class="text-center">Full Name</div></th>
            <th class="hidden-tablet"><div class="text-center">Organization</div></th>
            <th><div class="text-center">Privilege</div></th>
            <th><div class="text-center">Status</div></th>
            <th v-if="showActions" class="user-directory__actions-col"><div class="text-center">Actions</div></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in paginatedRows" :key="`user-${row.id}`" :class="{ danger: row.locked }">
            <td><div class="text-center"><a :href="row.show_url">{{ row.login }}</a></div></td>
            <td class="hidden-phone"><div class="text-center">{{ row.email }}</div></td>
            <td class="hidden-tablet"><div class="text-center">{{ row.realname }}</div></td>
            <td class="hidden-tablet"><div class="text-center">{{ row.organization }}</div></td>
            <td><div class="text-center">{{ row.privilege }}</div></td>
            <td>
              <div class="text-center">
                <span class="app-status-chip" :class="statusChipClass(row)">
                  {{ row.account_status || (row.locked ? 'Locked' : 'Active') }}
                </span>
              </div>
            </td>
            <td v-if="showActions" class="user-directory__actions-col col-text-center">
              <div v-if="row.actions" class="user-directory__menu-wrap">
                <button
                  type="button"
                  class="user-directory__gear"
                  :aria-expanded="openMenuId === row.id ? 'true' : 'false'"
                  @click.stop="toggleMenu(row, $event)"
                >
                  <AppIcon name="settings" />
                  <AppIcon name="chevron-down" class="user-directory__gear-caret" />
                </button>

                <div
                  v-if="openMenuId === row.id"
                  class="user-directory__menu"
                  :style="menuStyle"
                  @click.stop
                >
                  <a v-if="row.actions.edit_url" :href="row.actions.edit_url" class="user-directory__menu-link user-directory__menu-link--edit">
                    <AppIcon name="edit" />
                    <span>Edit</span>
                  </a>
                  <button
                    v-if="row.actions.lock_url && !row.locked"
                    type="button"
                    class="user-directory__menu-link user-directory__menu-link--delete"
                    @click="handleLock(row)"
                  >
                    <AppIcon name="lock" />
                    <span>Lock</span>
                  </button>
                  <button
                    v-if="row.actions.unlock_url && row.locked"
                    type="button"
                    class="user-directory__menu-link user-directory__menu-link--edit"
                    @click="handleUnlock(row)"
                  >
                    <AppIcon name="unlock" />
                    <span>Unlock</span>
                  </button>
                  <button
                    v-if="row.actions.destroy_url"
                    type="button"
                    class="user-directory__menu-link user-directory__menu-link--delete"
                    @click="handleDelete(row)"
                  >
                    <AppIcon name="delete" />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            </td>
          </tr>
          <tr v-if="filteredRows.length === 0">
            <td class="text-center text-muted" :colspan="showActions ? 7 : 6">No users matched the current filter.</td>
          </tr>
        </tbody>
      </table>
    </div>

  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import AppIcon from './AppIcon.vue'
import AppPagination from './AppPagination.vue'

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({ rows: [] })
  }
})

const rowsState = ref(props.initialData?.rows || [])
const rows = computed(() => rowsState.value)
const showActions = computed(() => rows.value.some((row) => row.actions))
const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
const accountKeyword = ref('')
const realnameKeyword = ref('')
const privilegeFilter = ref('')
const statusFilter = ref('')
const privilegePickerOpen = ref(false)
const statusPickerOpen = ref(false)
const privilegePickerRef = ref(null)
const statusPickerRef = ref(null)
const currentPage = ref(1)
const perPage = 20
const openMenuId = ref(null)
const menuStyle = ref({})

function sccShowToast(level, message) {
  window.sccStatusShell?.sccShowToast?.(level, message)
}

function statusChipClass(row) {
  const status = (row?.account_status || (row?.locked ? 'Locked' : 'Active')).toString().toLowerCase()
  return status === 'locked' ? 'is-down' : 'is-up'
}

const privilegeOptions = computed(() => {
  const byLevel = new Map()
  rows.value.forEach((row) => {
    const label = row.privilege
    const level = Number(row.privilege_level)
    if (!label || !Number.isFinite(level)) {
      return
    }
    if (!byLevel.has(level)) {
      byLevel.set(level, label)
    }
  })

  return Array.from(byLevel.entries())
    .sort((a, b) => a[0] - b[0])
    .map((entry) => entry[1])
})
const statusOptions = computed(() => {
  const set = new Set(['Active', 'Locked'])
  rows.value.forEach((row) => {
    const status = row.account_status || (row.locked ? 'Locked' : 'Active')
    if (status) {
      set.add(status)
    }
  })
  return ['Active', 'Locked', ...Array.from(set).filter((value) => value !== 'Active' && value !== 'Locked').sort()]
})
const privilegeTriggerLabel = computed(() => privilegeFilter.value || 'All')
const statusTriggerLabel = computed(() => statusFilter.value || 'All')

const filteredRows = computed(() => {
  const normalizedAccountKeyword = accountKeyword.value.trim().toLowerCase()
  const normalizedRealnameKeyword = realnameKeyword.value.trim().toLowerCase()
  return rows.value.filter((row) => {
    const matchesAccount =
      normalizedAccountKeyword === '' ||
      row.login?.toLowerCase().includes(normalizedAccountKeyword)

    const matchesRealname =
      normalizedRealnameKeyword === '' ||
      row.realname?.toLowerCase().includes(normalizedRealnameKeyword)

    const matchesPrivilege =
      privilegeFilter.value === '' || row.privilege === privilegeFilter.value

    const status = row.account_status || (row.locked ? 'Locked' : 'Active')
    const matchesStatus =
      statusFilter.value === '' || status === statusFilter.value

    return matchesAccount && matchesRealname && matchesPrivilege && matchesStatus
  })
})

const totalPages = computed(() => Math.max(1, Math.ceil(filteredRows.value.length / perPage)))

const pagination = computed(() => ({
  current_page: currentPage.value,
  total_pages: totalPages.value,
  prev_page: currentPage.value > 1 ? currentPage.value - 1 : null,
  next_page: currentPage.value < totalPages.value ? currentPage.value + 1 : null
}))

const paginatedRows = computed(() => {
  const start = (currentPage.value - 1) * perPage
  return filteredRows.value.slice(start, start + perPage)
})

watch([accountKeyword, realnameKeyword, privilegeFilter, statusFilter], () => {
  currentPage.value = 1
  openMenuId.value = null
})

watch(
  () => props.initialData,
  (nextValue) => {
    rowsState.value = nextValue?.rows || []
    currentPage.value = 1
    openMenuId.value = null
    menuStyle.value = {}
  }
)

watch(totalPages, (nextTotalPages) => {
  if (currentPage.value > nextTotalPages) {
    currentPage.value = nextTotalPages
  }
})

function handlePaginationClick(event) {
  const link = event.target.closest('.pagination a[data-page]')
  if (!link) {
    return
  }

  const nextPage = Number(link.dataset.page)
  if (!Number.isFinite(nextPage) || nextPage < 1 || nextPage > totalPages.value) {
    return
  }

  currentPage.value = nextPage
  openMenuId.value = null
  menuStyle.value = {}
}

function menuActionCount(actions) {
  let count = 0
  if (actions?.edit_url) count += 1
  if (actions?.lock_url) count += 1
  if (actions?.unlock_url) count += 1
  if (actions?.destroy_url) count += 1
  return count
}

function toggleMenu(row, event) {
  const rowId = row?.id
  if (openMenuId.value === rowId) {
    openMenuId.value = null
    menuStyle.value = {}
    return
  }

  const trigger = event?.currentTarget
  const rect = trigger?.getBoundingClientRect()

  if (!rect) {
    openMenuId.value = rowId
    menuStyle.value = {}
    return
  }

  const menuWidth = 150
  const actionCount = Math.max(1, menuActionCount(row?.actions))
  const menuHeight = 20 + actionCount * 38
  const gap = 8
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const horizontalPadding = 12
  const verticalPadding = 12
  const openUpward = rect.bottom + menuHeight + gap > viewportHeight && rect.top - menuHeight - gap >= verticalPadding

  let left = rect.right - menuWidth
  left = Math.max(horizontalPadding, Math.min(left, viewportWidth - menuWidth - horizontalPadding))

  let top = openUpward ? rect.top - menuHeight - gap : rect.bottom + gap
  top = Math.max(verticalPadding, Math.min(top, viewportHeight - menuHeight - verticalPadding))

  openMenuId.value = rowId
  menuStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`
  }
}

function closeMenuOnOutsideClick() {
  openMenuId.value = null
  menuStyle.value = {}
  privilegePickerOpen.value = false
  statusPickerOpen.value = false
}

function togglePrivilegePicker() {
  privilegePickerOpen.value = !privilegePickerOpen.value
  if (privilegePickerOpen.value) {
    statusPickerOpen.value = false
    openMenuId.value = null
    menuStyle.value = {}
  }
}

function toggleStatusPicker() {
  statusPickerOpen.value = !statusPickerOpen.value
  if (statusPickerOpen.value) {
    privilegePickerOpen.value = false
    openMenuId.value = null
    menuStyle.value = {}
  }
}

function selectPrivilege(value) {
  privilegeFilter.value = value
  privilegePickerOpen.value = false
}

function selectStatus(value) {
  statusFilter.value = value
  statusPickerOpen.value = false
}

async function handleDelete(row) {
  const actions = row.actions || {}
  if (!actions.destroy_url) {
    return
  }

  if (actions.destroy_confirm_message && !window.confirm(actions.destroy_confirm_message)) {
    return
  }

  try {
    const payload = await requestJson(actions.destroy_url, { method: 'DELETE' })
    if (payload?.level && payload?.message) {
      sccShowToast(payload.level, payload.message)
    }

    openMenuId.value = null
    menuStyle.value = {}
    const deletedId = String(row.id)
    rowsState.value = rowsState.value.filter((entry) => String(entry.id) !== deletedId)
  } catch (error) {
    sccShowToast('danger', error.message || 'Failed to delete user.')
  }
}

async function handleUnlock(row) {
  const actions = row.actions || {}
  if (!actions.unlock_url) {
    return
  }

  if (actions.unlock_confirm_message && !window.confirm(actions.unlock_confirm_message)) {
    return
  }

  try {
    const payload = await requestJson(actions.unlock_url, { method: 'PATCH' })
    if (payload?.level && payload?.message) {
      sccShowToast(payload.level, payload.message)
    }

    openMenuId.value = null
    menuStyle.value = {}
    const targetId = String(row.id)
    rowsState.value = rowsState.value.map((entry) => {
      if (String(entry.id) !== targetId) {
        return entry
      }

      return {
        ...entry,
        locked: false,
        account_status: 'Active',
        actions: { ...(entry.actions || {}) }
      }
    })
  } catch (error) {
    sccShowToast('danger', error.message || 'Failed to unlock user.')
  }
}

async function handleLock(row) {
  const actions = row.actions || {}
  if (!actions.lock_url) {
    return
  }

  if (actions.lock_confirm_message && !window.confirm(actions.lock_confirm_message)) {
    return
  }

  try {
    const payload = await requestJson(actions.lock_url, { method: 'PATCH' })
    if (payload?.level && payload?.message) {
      sccShowToast(payload.level, payload.message)
    }

    openMenuId.value = null
    menuStyle.value = {}
    const targetId = String(row.id)
    rowsState.value = rowsState.value.map((entry) => {
      if (String(entry.id) !== targetId) {
        return entry
      }

      return {
        ...entry,
        locked: true,
        account_status: 'Locked',
        actions: { ...(entry.actions || {}) }
      }
    })
  } catch (error) {
    sccShowToast('danger', error.message || 'Failed to lock user.')
  }
}

async function requestJson(url, options = {}) {
  const method = (options.method || 'GET').toUpperCase()
  const headers = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...(options.headers || {})
  }
  const fetchOptions = {
    method,
    headers,
    credentials: 'same-origin'
  }

  if (csrfToken && method !== 'GET' && method !== 'HEAD') {
    headers['X-CSRF-Token'] = csrfToken
  }

  const response = await fetch(url, fetchOptions)
  const text = await response.text()
  let payload = null

  if (text) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = null
    }
  }

  if (!response.ok) {
    throw new Error((payload && payload.message) || text || 'Request failed.')
  }

  return payload
}

onMounted(() => {
  document.addEventListener('click', closeMenuOnOutsideClick)
  window.addEventListener('resize', closeMenuOnOutsideClick)
  window.addEventListener('scroll', closeMenuOnOutsideClick, true)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', closeMenuOnOutsideClick)
  window.removeEventListener('resize', closeMenuOnOutsideClick)
  window.removeEventListener('scroll', closeMenuOnOutsideClick, true)
})
</script>

<style scoped>
.user-search-select {
  position: relative;
}

.user-search-select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 170px;
}

.user-search-select-trigger-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-search-select-panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 12;
  width: 100%;
  min-width: 170px;
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #d4dde8;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.14);
  padding: 6px;
}

.user-search-select-option {
  width: 100%;
  text-align: left;
  border: 0;
  border-radius: 8px;
  background: transparent;
  padding: 8px 10px;
  color: #0f172a;
  font-size: 13px;
}

.user-search-select-option:hover,
.user-search-select-option:focus {
  background: #eff6ff;
  color: #1d4ed8;
  outline: none;
}
</style>
